<template>
  <div class="color-gradient-editor">
    <div class="color-gradient-editor__label" style="flex-grow: 1">
      {{ label }}
    </div>
    <slot />
    <div
      :class="[
        'el-color-picker el-color-picker--mini setting-color color-gradient-editor__picker',
        disabled ? 'is-disabled' : ''
      ]"
    >
      <div class="el-color-picker__mask" v-if="disabled"></div>
      <a-dropdown
        :trigger="['click']"
        :disabled="disabled"
        @visibleChange="handleTrigger"
        :visible="showPicker"
      >
        <div class="el-color-picker__trigger">
          <span
            class="el-color-picker__color"
            :class="{ 'is-alpha': showAlpha }"
          >
            <span
              class="el-color-picker__color-inner"
              :style="pickerPreviewStyle"
            />
            <span
              class="el-color-picker__empty el-icon-close"
              v-if="!value && !showPanelColor"
            />
          </span>
          <span
            class="el-color-picker__icon el-icon-arrow-down"
            v-show="value || showPanelColor"
          />
        </div>

        <pass-current-color
          slot="overlay"
          class="color-gradient-overlay"
          :currentColor="colorValue"
        >
          <div class="color-gradient-overlay__types">
            <div
              class="
                color-gradient-overlay__type color-gradient-overlay__type--color
              "
              :class="{
                'color-gradient-overlay__type--selected': type === 'color'
              }"
              @click="changeType('color')"
            >
              纯色
            </div>
            <div
              class="
                color-gradient-overlay__type
                color-gradient-overlay__type--gradient
              "
              :class="{
                'color-gradient-overlay__type--selected': type === 'gradient',
                'text-gradient-disabled': gradientDisabled
              }"
              @click="changeType('gradient')"
            >
              渐变
            </div>
          </div>
          <template v-if="type === 'gradient'">
            <div class="color-gradient-overlay__section">
              <label-option
                v-model="editingGradient.type"
                :optionList="typeList"
                label="渐变类型"
              />
              <template v-if="editingGradient.type === 'linear'">
                <label-input
                  v-model="editingGradient.angle"
                  label="渐变角度"
                  :deg="true"
                />
              </template>
              <div class="color-gradient-stops">
                <div
                  class="color-gradient-stops__gradient"
                  :style="gradientStyle"
                >
                  <el-tooltip content="渐变起始" placement="top">
                    <div
                      class="color-gradient-stops__stop"
                      :class="{
                        'color-gradient-stops__stop--selected':
                          active === 'from'
                      }"
                      :style="{
                        background: editingGradient.from,
                        left: 0
                      }"
                      @click="handleStopClick('from')"
                    />
                  </el-tooltip>
                  <el-tooltip content="渐变结束" placement="top">
                    <div
                      class="color-gradient-stops__stop"
                      :class="{
                        'color-gradient-stops__stop--selected': active === 'to'
                      }"
                      :style="{
                        background: editingGradient.to,
                        left: '100%'
                      }"
                      @click="handleStopClick('to')"
                    />
                  </el-tooltip>
                </div>
              </div>
            </div>
          </template>
          <sv-panel :color="color" class="color-gradient-overlay__sv" />
          <div class="color-gradient-overlay__sliders">
            <hue-slider :color="color" class="color-gradient-overlay__item" />
            <div
              class="
                color-gradient-overlay__preview
                color-gradient-overlay__preview--alpha
              "
            >
              <div
                class="color-gradient-overlay__preview-color"
                :style="{
                  backgroundColor: displayedColor
                }"
              />
            </div>
          </div>
          <div class="color-gradient-overlay__footer">
            <a-input
              class="color-gradient-overlay__input"
              v-model="inputValue"
              @change.native="handleChange"
              @keydown.native.enter="handleChange"
            />
            <predefine-panel
              :color="color"
              :colors="predefineColors"
              class="color-gradient-overlay__predefine"
            />
            <a-button
              @click="showMaterialColor"
              class="color-gradient-overlay__color-btn"
              type="link"
            >
              <a-icon :component="ColorPickerIcon" />
              素材拾色器…
            </a-button>
            <p class="color-gradient-overlay__btns">
              <a-button class="color-gradient-overlay__btn" @click="clearValue">
                清空
              </a-button>
              <a-button
                type="primary"
                class="color-gradient-overlay__btn"
                @click="done"
              >
                完成
              </a-button>
            </p>
          </div>
        </pass-current-color>
      </a-dropdown>
    </div>
  </div>
</template>

<script>
import Color from 'element-ui/packages/color-picker/src/color';
import Emitter from 'element-ui/src/mixins/emitter';
import HueSlider from 'element-ui/packages/color-picker/src/components/hue-slider.vue';
import SVPanel from 'element-ui/packages/color-picker/src/components/sv-panel.vue';
import Predefine from 'element-ui/packages/color-picker/src/components/predefine.vue';
import LabelOption from './LabelOption';
import LabelInput from './LabelInput';
import { PassCurrentColor, getPredefineColors, ColorPickerIcon } from './ColorGradientUtils';

import './ColorGradientEditor.vue?vue&type=style&index=0&lang=css';
import { mapState } from 'vuex';

export default {
  name: 'ElColorPicker',

  mixins: [Emitter],

  model: {
    prop: 'value',
    event: 'change'
  },

  components: {
    HueSlider,
    'sv-panel': SVPanel,
    LabelOption,
    LabelInput,
    'predefine-panel': Predefine,
    PassCurrentColor
  },

  props: {
    value: String,
    showAlpha: Boolean,
    colorFormat: String,
    disabled: Boolean,
    size: String,
    popperClass: String,
    predefine: Array,
    label: String,
    gradient: Object,
    gradientDisabled: Boolean
  },

  computed: {
    ...mapState(['materialColorVisible', 'materialColor']),
    displayedColor() {
      if (!this.value && !this.showPanelColor) {
        return 'transparent';
      }

      return this.displayedRgb(this.color, this.showAlpha);
    },
    colorValue() {
      return this.showPanelColor ? this.color.value : ''
    },

    usedValue() {
      if (this.type === 'color') {
        return this.value;
      }

      return this.editingGradient[this.active];
    },
    gradientStyle() {
      if (this.type === 'color') {
        return '';
      }

      return {
        backgroundImage: `linear-gradient(to right,${this.editingGradient.from},${this.editingGradient.to})`
      }
    },
    pickerPreviewStyle() {
      if (this.type === 'color') {
        return {
          backgroundColor: this.displayedColor
        };
      }

      if (this.editingGradient.type === 'linear') {
        return {
          backgroundImage: `linear-gradient(${this.editingGradient.angle + 90}deg,${this.editingGradient.from},${this.editingGradient.to})`
        };
      }
      return {
        backgroundImage: `radial-gradient(circle,${this.editingGradient.from},${this.editingGradient.to})`
      };
    },
    prevGradient: {
      get() {
        return this.$store.state.prevGradients.text || null;
      },
      set(value) {
        this.$set(this.$store.state.prevGradients, 'text', value);
      }
    }
  },

  watch: {
    usedValue(val) {
      if (!val) {
        this.showPanelColor = false;
      } else {
        if (val !== this.color.value) {
          this.color.fromString(val);
        }

        this.showPanelColor = true;
      }
    },
    color: {
      deep: true,
      handler() {
        this.showPanelColor = true;

        this.$nextTick(() => {
          if (this.type === 'gradient') {
            this.editingGradient[this.active] = this.colorValue;
          }
        });
      }
    },
    displayedColor(val) {
      if (!this.showPicker) return;
      const currentValueColor = new Color({
        enableAlpha: this.showAlpha,
        format: this.colorFormat
      });
      currentValueColor.fromString(this.usedValue);

      const currentValueColorRgb = this.displayedRgb(currentValueColor, this.showAlpha);
      if (val !== currentValueColorRgb) {
        this.$emit('active-change', val);
      }
    },

    colorValue() {
      this.inputValue = this.colorValue;
    },
    gradient: {
      deep: true,
      handler(val) {
        if (val) {
          const gradientCopy = JSON.parse(JSON.stringify(val));
          this.type = 'gradient';
          this.color.fromString(val[this.active] || val.from);
          this.editingGradient = gradientCopy;
        } else {
          this.type = 'color';
          if (this.prevGradient) {
            const gradientCopy = JSON.parse(JSON.stringify(this.prevGradient));
            this.editingGradient = gradientCopy;
          }
        }
      }
    },
    materialColor(val) {
      if (this.showPicker) {
        this.inputValue = val;
        this.handleChange();
      }
    }
  },

  methods: {
    handleTrigger(value) {
      if (!this.materialColorVisible) {
        this.showPicker = value;
        if (!value) {
          this.resetColor();
        }
      }
    },
    changeType(type) {
      if (!this.gradientDisabled) {
        this.type = type;
      }
    },
    handleStopClick(name) {
      this.active = name;
    },

    clearValue() {
      this.$emit('input', null);
      this.$emit('change', null);
      this.showPanelColor = false;
      this.showPicker = false;
      this.resetColor();
    },
    resetColor() {
      // TODO: handle the correct panel
      if (this.gradient) {
        const gradientCopy = JSON.parse(JSON.stringify(this.gradient));
        this.editingGradient = {
          ...gradientCopy,
          type: gradientCopy.type === 'none' ? 'linear' : gradientCopy.type
        };
      }
      this.$nextTick(_ => {
        this.type = this.gradient && this.gradient.type !== 'none' ? 'gradient' : 'color';
        if (this.usedValue) {
          this.color.fromString(this.usedValue);
        } else {
          this.showPanelColor = false;
        }
      });
    },
    displayedRgb(color, showAlpha) {
      if (!(color instanceof Color)) {
        throw Error('color should be instance of Color Class');
      }

      const { r, g, b } = color.toRgb();
      return showAlpha
        ? `rgba(${r}, ${g}, ${b}, ${color.get('alpha') / 100})`
        : `rgb(${r}, ${g}, ${b})`;
    },
    handleChange() {
      this.color.fromString(this.inputValue);
    },
    done() {
      if (this.type === 'color') {
        this.$emit('change', this.colorValue);
        if (this.gradient) {
          this.prevGradient = this.gradient;
          this.$emit('update:gradient', null);
        }
      } else {
        this.$emit('update:gradient', this.editingGradient);
        this.prevGradient = this.editingGradient;
      }
      this.$nextTick(() => {
        this.handleTrigger(false);
      });
    },
    showMaterialColor() {
      this.$store.commit('handleMaterialColorVisible', true);
    }
  },

  mounted() {
    const value = this.value;

    if (this.gradient) {
      const gradientCopy = JSON.parse(JSON.stringify(this.gradient));
      this.type = 'gradient';
      this.color.fromString(this.gradient.from);
      this.editingGradient = gradientCopy;
    } else {
      if (value) {
        this.color.fromString(value);
      }
      if (this.prevGradient) {
        this.editingGradient = JSON.parse(JSON.stringify(this.prevGradient));
      }
    }

    this.$nextTick(() => {
      this.inputValue = this.colorValue;
    });

    document.body.addEventListener('mousemove', this.handleMousemove);
    document.body.addEventListener('mouseup', this.handleMouseup);
  },

  beforeDestroy() {
    document.body.removeEventListener('mousemove', this.handleMousemove);
    document.body.removeEventListener('mouseup', this.handleMouseup);
  },

  data() {
    const color = new Color({
      enableAlpha: this.showAlpha,
      format: this.colorFormat
    });

    return {
      color,
      showPicker: false,
      showPanelColor: false,
      type: 'color',
      inputValue: '',
      editingGradient: {
        type: 'linear',
        angle: 0,
        from: '#007eff',
        to: '#ee44cc'
      },
      active: 'from',
      typeList: [
        { name: '线性渐变', value: 'linear' },
        { name: '径向渐变', value: 'radial' }
      ],
      predefineColors: getPredefineColors(false),
      ColorPickerIcon
    };
  }
};
</script>
<style scoped>
.text-gradient-disabled {
  opacity: 0.3;
  cursor: default;
}
</style>